<template>
	<v-card dark outlined class="stats-oee-card pa-3" style="background-color:#1E1E1E; border-color:#2D2D2D;">
		<div class="d-flex align-center justify-space-between mb-2">
			<div class="caption grey--text text-uppercase d-flex align-center" style="letter-spacing:0.05em;">
				<v-icon small left color="#26C6DA">mdi-chart-donut</v-icon>{{ $ml.get('alarm_stats_oee') }}
				<v-tooltip bottom max-width="300">
					<template v-slot:activator="{ on }">
						<v-icon x-small color="grey" class="ml-1" v-on="on">mdi-information-outline</v-icon>
					</template>
					<span>OEE (Overall Equipment Effectiveness) = Availability × Performance × Quality. Measures how well the line is producing versus its full potential during the shift. Typical target: ≥ 85%.</span>
				</v-tooltip>
			</div>
			<!-- Overall OEE badge -->
			<v-chip small :color="overallColor" dark class="font-weight-bold">
				{{ formatPct(overall) }}%
			</v-chip>
		</div>

		<!-- Big OEE number -->
		<div class="text-center mb-3">
			<div class="text-h3 font-weight-bold" :style="{ color: overallColorHex }">
				<animated-number :value="overall" :multiplier="100" :decimals="1"></animated-number>%
			</div>
			<div class="caption grey--text">{{ $ml.get('alarm_stats_oee') }}</div>
		</div>

		<!-- 3 sub-metric cards -->
		<v-row no-gutters class="mb-3">
			<v-col cols="4" class="pr-1">
				<v-card outlined class="pa-2 text-center" style="background-color:#2D2D2D; border-color:#424242;">
					<v-tooltip bottom max-width="280">
						<template v-slot:activator="{ on }">
							<div class="caption grey--text oee-help" style="font-size:10px; letter-spacing:0.04em;" v-on="on">{{ $ml.get('alarm_stats_availability') }}</div>
						</template>
						<span>Availability: % of the elapsed shift time the line was running (no stoppages). = (elapsed time − downtime) ÷ elapsed time.</span>
					</v-tooltip>
					<div class="subtitle-2 font-weight-bold mt-1" style="color:#66BB6A;">
						<animated-number :value="availability" :multiplier="100" :decimals="1"></animated-number>%
					</div>
				</v-card>
			</v-col>
			<v-col cols="4" class="px-1">
				<v-card outlined class="pa-2 text-center" style="background-color:#2D2D2D; border-color:#424242;">
					<v-tooltip bottom max-width="280">
						<template v-slot:activator="{ on }">
							<div class="caption grey--text oee-help" style="font-size:10px; letter-spacing:0.04em;" v-on="on">{{ $ml.get('alarm_stats_performance') }}</div>
						</template>
						<span>Performance: actual speed vs the target. = units ÷ (target per minute × run time). Capped at 100% when you beat the target.</span>
					</v-tooltip>
					<div class="subtitle-2 font-weight-bold mt-1" style="color:#FFA726;">
						<animated-number :value="performance" :multiplier="100" :decimals="1"></animated-number>%
					</div>
				</v-card>
			</v-col>
			<v-col cols="4" class="pl-1">
				<v-card outlined class="pa-2 text-center" style="background-color:#2D2D2D; border-color:#424242;">
					<v-tooltip bottom max-width="280">
						<template v-slot:activator="{ on }">
							<div class="caption grey--text oee-help" style="font-size:10px; letter-spacing:0.04em;" v-on="on">{{ $ml.get('alarm_stats_quality') }}</div>
						</template>
						<span>Quality: % of good units. = good ÷ total produced.</span>
					</v-tooltip>
					<div class="subtitle-2 font-weight-bold mt-1" style="color:#26C6DA;">
						<animated-number :value="quality" :multiplier="100" :decimals="1"></animated-number>%
					</div>
				</v-card>
			</v-col>
		</v-row>

		<!-- OEE Target progress bar -->
		<div>
			<div class="d-flex justify-space-between caption mb-1">
				<span class="grey--text">{{ $ml.get('alarm_stats_oee_target') }}</span>
				<span :style="{ color: overallColorHex }">{{ formatPct(overall) }} / 85%</span>
			</div>
			<v-progress-linear
				:value="oeeTargetPct"
				:color="overallColor"
				background-color="#2D2D2D"
				height="8"
				rounded
			></v-progress-linear>
		</div>
	</v-card>
</template>

<script>
import AnimatedNumber from './AnimatedNumber.vue';

export default {
	name : 'StatsOee',

	components : {
		AnimatedNumber,
	},

	props : {
		availability : {
			type    : Number,
			default : 0,
		},
		performance : {
			type    : Number,
			default : 0,
		},
		quality : {
			type    : Number,
			default : 0,
		},
		overall : {
			type    : Number,
			default : 0,
		},
	},

	computed : {
		// Backend sends 0-1 fractions; compare thresholds as fractions too
		overallColor() {
			if (this.overall >= 0.85) return 'success';
			if (this.overall >= 0.60) return 'warning';
			return 'error';
		},

		overallColorHex() {
			if (this.overall >= 0.85) return '#66BB6A';
			if (this.overall >= 0.60) return '#FFA726';
			return '#EF5350';
		},

		// Progress bar scaled to 85% target (capped at 100%)
		// overall is 0-1, target is 0.85
		oeeTargetPct() {
			return Math.min(100, Math.round((this.overall / 0.85) * 100));
		},
	},

	methods : {
		// Convert 0-1 fraction to display percentage string
		formatPct(value) {
			if (value === null || value === undefined) return '0.0';
			return (Number(value) * 100).toFixed(1);
		},
	},
};
</script>

<style scoped>
.stats-oee-card {
	min-height: 200px;
}

.oee-help {
	cursor: help;
	border-bottom: 1px dotted #555;
	display: inline-block;
}
</style>
